import { useCallback, useEffect, useMemo, useState } from 'react';
import { USER_ROLE, type FaqAudienceRole, type FaqItem } from '@fdh/shared';
import { adminApi } from '../../lib/adminApi';
import { useLanguage } from '../../contexts/LanguageContext';

type FaqPayload = { faqs: FaqItem[] };

type FormState = {
  audienceRole: FaqAudienceRole;
  questionZhHant: string;
  answerZhHant: string;
  questionZhHans: string;
  answerZhHans: string;
  questionEn: string;
  answerEn: string;
  sortOrder: number;
  isActive: boolean;
};

const emptyFormState: FormState = {
  audienceRole: USER_ROLE.HELPER,
  questionZhHant: '',
  answerZhHant: '',
  questionZhHans: '',
  answerZhHans: '',
  questionEn: '',
  answerEn: '',
  sortOrder: 0,
  isActive: true,
};

const toFormState = (item: FaqItem): FormState => ({
  audienceRole: item.audienceRole,
  questionZhHant: item.questionZhHant,
  answerZhHant: item.answerZhHant,
  questionZhHans: item.questionZhHans,
  answerZhHans: item.answerZhHans,
  questionEn: item.questionEn,
  answerEn: item.answerEn,
  sortOrder: item.sortOrder,
  isActive: item.isActive,
});

export const AdminFaqsPage = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState<FaqItem[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyFormState);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await adminApi.get<FaqPayload>(
        `/admin/faqs?includeInactive=${includeInactive ? 'true' : 'false'}`,
      );
      if (res.success && res.data?.faqs) {
        setItems(res.data.faqs);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
    }
  }, [includeInactive, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const groupedItems = useMemo(
    () => ({
      helper: items.filter((item) => item.audienceRole === USER_ROLE.HELPER),
      employer: items.filter((item) => item.audienceRole === USER_ROLE.EMPLOYER),
    }),
    [items],
  );

  const validateForm = (): boolean => {
    if (form.questionZhHant.trim() === '' || form.answerZhHant.trim() === '') {
      return false;
    }
    if (form.questionZhHans.trim() === '' || form.answerZhHans.trim() === '') {
      return false;
    }
    if (form.questionEn.trim() === '' || form.answerEn.trim() === '') {
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setError(t('admin.faqFormIncomplete'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await adminApi.put<FaqItem>(`/admin/faqs/${editingId}`, form);
      } else {
        await adminApi.post<FaqItem>('/admin/faqs', form);
      }
      setEditingId(null);
      setForm(emptyFormState);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: FaqItem) => {
    setEditingId(item.id);
    setForm(toFormState(item));
  };

  const handleDelete = async (id: string) => {
    setError('');
    try {
      await adminApi.delete<{ deleted: boolean }>(`/admin/faqs/${id}`);
      if (editingId === id) {
        setEditingId(null);
        setForm(emptyFormState);
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm(emptyFormState);
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-white">{t('admin.nav.faqs')}</h1>
      <p className="mt-1 text-sm text-slate-400">{t('admin.faqsSubtitle')}</p>

      {error ? (
        <p className="mt-4 text-sm text-red-400" role="alert">
          {error}
        </p>
      ) : null}

      <section className="mt-8 rounded-lg border border-slate-800 bg-slate-900/60 p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-medium text-white">
            {editingId ? t('admin.faqEditTitle') : t('admin.faqCreateTitle')}
          </h2>
          {editingId ? (
            <button
              type="button"
              onClick={handleCancelEdit}
              className="rounded-md border border-slate-700 px-3 py-1 text-xs text-slate-200 hover:bg-slate-800"
            >
              {t('admin.cancel')}
            </button>
          ) : null}
        </div>
        <div className="grid gap-3">
          <select
            value={form.audienceRole}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, audienceRole: e.target.value as FaqAudienceRole }))
            }
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          >
            <option value={USER_ROLE.HELPER}>{t('faq.tabHelper')}</option>
            <option value={USER_ROLE.EMPLOYER}>{t('faq.tabEmployer')}</option>
          </select>
          <input
            type="number"
            min={0}
            value={form.sortOrder}
            onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: Number(e.target.value) || 0 }))}
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
            placeholder={t('admin.faqSortOrder')}
          />
          <label className="flex items-center gap-2 text-sm text-slate-200">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
            />
            {t('admin.faqIsActive')}
          </label>

          <textarea
            rows={2}
            value={form.questionZhHant}
            onChange={(e) => setForm((prev) => ({ ...prev, questionZhHant: e.target.value }))}
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
            placeholder={t('admin.faqQuestionZhHant')}
          />
          <textarea
            rows={3}
            value={form.answerZhHant}
            onChange={(e) => setForm((prev) => ({ ...prev, answerZhHant: e.target.value }))}
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
            placeholder={t('admin.faqAnswerZhHant')}
          />
          <textarea
            rows={2}
            value={form.questionZhHans}
            onChange={(e) => setForm((prev) => ({ ...prev, questionZhHans: e.target.value }))}
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
            placeholder={t('admin.faqQuestionZhHans')}
          />
          <textarea
            rows={3}
            value={form.answerZhHans}
            onChange={(e) => setForm((prev) => ({ ...prev, answerZhHans: e.target.value }))}
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
            placeholder={t('admin.faqAnswerZhHans')}
          />
          <textarea
            rows={2}
            value={form.questionEn}
            onChange={(e) => setForm((prev) => ({ ...prev, questionEn: e.target.value }))}
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
            placeholder={t('admin.faqQuestionEn')}
          />
          <textarea
            rows={3}
            value={form.answerEn}
            onChange={(e) => setForm((prev) => ({ ...prev, answerEn: e.target.value }))}
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
            placeholder={t('admin.faqAnswerEn')}
          />

          <button
            type="button"
            disabled={saving}
            className="rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-60"
            onClick={() => void handleSubmit()}
          >
            {saving ? t('admin.saving') : editingId ? t('admin.update') : t('admin.create')}
          </button>
        </div>
      </section>

      <section className="mt-8">
        <label className="inline-flex items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          {t('admin.faqShowInactive')}
        </label>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
            <h3 className="text-sm font-semibold text-white">{t('faq.tabHelper')}</h3>
            <ul className="mt-3 space-y-2">
              {groupedItems.helper.map((item) => (
                <li key={item.id} className="rounded-md border border-slate-700 p-3 text-sm text-slate-200">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-400">
                      #{item.sortOrder} · {item.isActive ? t('admin.active') : t('admin.inactive')}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(item)}
                        className="rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
                      >
                        {t('admin.edit')}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(item.id)}
                        className="rounded border border-red-700/50 px-2 py-1 text-xs text-red-300 hover:bg-red-900/30"
                      >
                        {t('admin.remove')}
                      </button>
                    </div>
                  </div>
                  <p className="mt-2 font-medium text-white">{item.questionZhHant}</p>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
            <h3 className="text-sm font-semibold text-white">{t('faq.tabEmployer')}</h3>
            <ul className="mt-3 space-y-2">
              {groupedItems.employer.map((item) => (
                <li key={item.id} className="rounded-md border border-slate-700 p-3 text-sm text-slate-200">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-400">
                      #{item.sortOrder} · {item.isActive ? t('admin.active') : t('admin.inactive')}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(item)}
                        className="rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
                      >
                        {t('admin.edit')}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(item.id)}
                        className="rounded border border-red-700/50 px-2 py-1 text-xs text-red-300 hover:bg-red-900/30"
                      >
                        {t('admin.remove')}
                      </button>
                    </div>
                  </div>
                  <p className="mt-2 font-medium text-white">{item.questionZhHant}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};
